import { useState, useMemo, useEffect, useRef } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, collection, addDoc, deleteDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import { AuthPage } from "./components/AuthPage";
import { C, MUSCLE_GROUPS, MUSCLE_SUBGROUPS, TIER_WEIGHT, EXERCISE_LOOKUP, initialWorkouts, initialPrograms } from "./constants";
import { toBaseWeight } from "./utils/units";
import { Dashboard }      from "./components/Dashboard";
import { ProgramsTab }    from "./components/ProgramsTab";
import { HistoryTab }     from "./components/HistoryTab";
import { ProgressionTab } from "./components/ProgressionTab";
import { WorkoutRunner }  from "./components/WorkoutRunner";
import { PlanBuilder }    from "./components/PlanBuilder";
import { ProgramBuilder } from "./components/ProgramBuilder";
import { WorkoutForm }    from "./components/WorkoutForm";
import { SettingsModal }  from "./components/SettingsModal";
import { ExerciseLibraryTab } from "./components/ExerciseLibraryTab";
import { Btn }            from "./components/ui";

export default function App() {
  const [workouts,       setWorkouts]       = useState(initialWorkouts);
  const [programs,       setPrograms]       = useState(initialPrograms);
  const [activeProgramId, setActiveProgramId] = useState(initialPrograms[0]?.id);
  const [tab,            setTab]            = useState("dashboard");
  const [filterType,     setFilterType]     = useState("all");
  const [showLog,        setShowLog]        = useState(false);
  const [showSettings,   setShowSettings]   = useState(false);
  const [editingPlan,    setEditingPlan]    = useState(null);   // { plan, programId }
  const [addingToProg,   setAddingToProg]   = useState(null);   // programId
  const [editingProg,    setEditingProg]    = useState(undefined); // program | null=new | undefined=closed
  const [activeRun,      setActiveRun]      = useState(null);
  const [lastRunPlanId,  setLastRunPlanId]  = useState(null);
  const [settings,       setSettings]       = useState({ weightUnit: "lbs", distUnit: "mi", advancedMode: false });
  const [customExercises, setCustomExercises] = useState([]);
  const [userExerciseOverrides, setUserExerciseOverrides] = useState({});

  const addCustomExercise = async (ex) => {
    if (!user) return;
    const lower = ex.name.toLowerCase();
    if (customExercises.some(e => e.name.toLowerCase() === lower)) return;
    try {
      await addDoc(collection(db, "users", user.uid, "customExercises"), {
        name: ex.name,
        muscleGroup: ex.muscleGroup,
        subgroup: ex.p || null,
        type: ex.type || "machine",
        secondary:   ex.s || [],
        stabilisers: ex.t || [],
        progressionDirection: ex.progressionDirection || "up",
        createdAt: serverTimestamp(),
      });
    } catch (e) { console.warn("Failed to add custom exercise:", e.message); }
  };
  const updateCustomExercise = async (prev, next) => {
    if (!user) return;
    const target = customExercises.find(e => e.name.toLowerCase() === prev.name.toLowerCase());
    if (!target?.id) return;
    try {
      await setDoc(doc(db, "users", user.uid, "customExercises", target.id), {
        name: next.name,
        muscleGroup: next.muscleGroup,
        subgroup: next.p || null,
        type: next.type || "machine",
        secondary:   next.s || [],
        stabilisers: next.t || [],
        progressionDirection: next.progressionDirection || "up",
      }, { merge: true });
    } catch (e) { console.warn("Failed to update custom exercise:", e.message); }
  };
  const deleteCustomExercise = async (ex) => {
    if (!user) return;
    const target = customExercises.find(e => e.name.toLowerCase() === ex.name.toLowerCase());
    if (!target?.id) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "customExercises", target.id));
    } catch (e) { console.warn("Failed to delete custom exercise:", e.message); }
  };

  const setExerciseOverride = async (exerciseId, name, override) => {
    if (!user) return;
    const docId = exerciseId || (name || "").toLowerCase();
    if (!docId) return;
    try {
      await setDoc(doc(db, "users", user.uid, "exerciseOverrides", docId), {
        exerciseId: exerciseId || null,
        name: (name || "").toLowerCase(),
        ...override,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) { console.warn("Failed to save override:", e.message); }
  };
  const resetExerciseOverride = async (exerciseId, name) => {
    if (!user) return;
    const docId = exerciseId || (name || "").toLowerCase();
    if (!docId) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "exerciseOverrides", docId));
    } catch (e) { console.warn("Failed to reset override:", e.message); }
  };

  const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  // ── Auth + Firestore ──
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const dataLoaded = useRef(false);
  const saveTimer  = useRef(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        dataLoaded.current = false;
        setDataLoading(true);
        setWorkouts(initialWorkouts);
        setPrograms(initialPrograms);
        setActiveProgramId(initialPrograms[0]?.id);
        setSettings({ weightUnit: "lbs", distUnit: "mi", advancedMode: false });
        setLastRunPlanId(null);
        setCustomExercises([]);
        setUserExerciseOverrides({});
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          if (snap.exists()) {
            const d = snap.data();
            if (d.workouts)                      setWorkouts(d.workouts);
            if (d.programs)                      setPrograms(d.programs);
            if (d.settings)                      setSettings(prev => ({ ...prev, ...d.settings }));
            if (d.activeProgramId !== undefined) setActiveProgramId(d.activeProgramId);
            if (d.lastRunPlanId   !== undefined) setLastRunPlanId(d.lastRunPlanId);
          }
        } catch (e) {
          console.warn("Firestore load failed:", e.message);
        }
        setDataLoading(false);
        setTimeout(() => { dataLoaded.current = true; }, 200);
      } else {
        setWorkouts(initialWorkouts);
        setPrograms(initialPrograms);
        setActiveProgramId(initialPrograms[0]?.id);
        setSettings({ weightUnit: "lbs", distUnit: "mi", advancedMode: false });
        setLastRunPlanId(null);
        setCustomExercises([]);
        setUserExerciseOverrides({});
      }
      setAuthLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user || !dataLoaded.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setDoc(doc(db, "users", user.uid), {
        workouts, programs, settings, activeProgramId, lastRunPlanId,
      }, { merge: true });
    }, 1500);
  }, [workouts, programs, settings, activeProgramId, lastRunPlanId, user]);

  // Exercise library subcollections — real-time listeners
  useEffect(() => {
    if (!user) {
      setCustomExercises([]);
      setUserExerciseOverrides({});
      return;
    }
    const customRef    = collection(db, "users", user.uid, "customExercises");
    const overridesRef = collection(db, "users", user.uid, "exerciseOverrides");

    const unsubCustom = onSnapshot(customRef, snap => {
      setCustomExercises(snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name,
          muscleGroup: data.muscleGroup,
          type: data.type,
          p: data.subgroup,
          s: data.secondary   || [],
          t: data.stabilisers || [],
          progressionDirection: data.progressionDirection || "up",
        };
      }));
    }, e => console.warn("customExercises listener:", e.message));

    const unsubOverrides = onSnapshot(overridesRef, snap => {
      const out = {};
      snap.docs.forEach(d => {
        const data = d.data();
        out[d.id] = {
          ...(data.useCustomIncrement !== undefined ? { useCustomIncrement: data.useCustomIncrement } : {}),
          ...(data.increment !== undefined && data.increment !== null ? { increment: data.increment } : {}),
          ...(data.subgroup ? { p: data.subgroup } : {}),
          ...(data.type ? { type: data.type } : {}),
        };
      });
      setUserExerciseOverrides(out);
    }, e => console.warn("exerciseOverrides listener:", e.message));

    return () => { unsubCustom(); unsubOverrides(); };
  }, [user]);

  // ── Program helpers ──
  const saveProgram = prog => setPrograms(prev =>
    prev.find(p => p.id === prog.id) ? prev.map(p => p.id === prog.id ? prog : p) : [...prev, prog]
  );
  const deleteProgram = id => {
    setPrograms(prev => prev.filter(p => p.id !== id));
    if (activeProgramId === id) setActiveProgramId(programs.filter(p => p.id !== id)[0]?.id || null);
  };
  const reorderPrograms = (from, to) => setPrograms(prev => {
    const arr = [...prev]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); return arr;
  });

  // ── Plan helpers ──
  const savePlan = (programId, plan) => setPrograms(prev => prev.map(prog => {
    if (prog.id !== programId) return prog;
    const exists = prog.plans.find(p => p.id === plan.id);
    return { ...prog, plans: exists ? prog.plans.map(p => p.id === plan.id ? plan : p) : [...prog.plans, plan] };
  }));
  const deletePlan = (programId, planId) => setPrograms(prev => prev.map(prog =>
    prog.id !== programId ? prog : { ...prog, plans: prog.plans.filter(p => p.id !== planId) }
  ));
  const reorderPlans = (programId, from, to) => setPrograms(prev => prev.map(prog => {
    if (prog.id !== programId) return prog;
    const arr = [...prog.plans]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    return { ...prog, plans: arr };
  }));
  const addRestDay = (programId) => savePlan(programId, { id: Date.now(), type: "rest", name: "Rest Day", exercises: [] });

  const startRun  = plan => { setLastRunPlanId(plan.id); setActiveRun(plan); };
  const finishRun = (log, elapsed) => {
    const today     = new Date().toISOString().split("T")[0];
    const sessionId = `session_${Date.now()}`;
    const entries   = log.flatMap(ex =>
      ex.completedSets
        .filter(cs => !cs.skipped)
        .map((cs) => ({
          id: Date.now() + Math.random(),
          date: today, type: "strength",
          exerciseId: ex.exerciseId || null,
          name: ex.name, muscleGroup: ex.muscleGroup,
          completionOrder: ex.completionOrder ?? null,
          isDropset: !!cs.isDropset,
          isMyoRep:  !!cs.isMyoRep,
          sets: 1, reps: cs.reps, weight: toBaseWeight(cs.weight, settings.weightUnit),
          sessionId, planName: activeRun.name, planEmoji: activeRun.emoji, elapsedSecs: elapsed,
        }))
    );
    setWorkouts(prev => [...entries, ...prev]);
    setActiveRun(null);
  };

  const dashStats = useMemo(() => {
    const now      = new Date();
    const todayStr = now.toISOString().split("T")[0];

    // Streak
    const workoutDates = new Set(workouts.map(w => w.date));
    let streak = 0;
    const d = new Date(now);
    if (!workoutDates.has(todayStr)) d.setDate(d.getDate() - 1);
    while (workoutDates.has(d.toISOString().split("T")[0])) { streak++; d.setDate(d.getDate() - 1); }

    // Recent PRs (last 14 days)
    const cutoff14 = new Date(now); cutoff14.setDate(now.getDate() - 14);
    const cutoff14Str      = cutoff14.toISOString().split("T")[0];
    const strengthWorkouts = workouts.filter(w => w.type === "strength" && w.weight > 0);
    const byExercise       = strengthWorkouts.reduce((acc, w) => { if (!acc[w.name]) acc[w.name] = []; acc[w.name].push(w); return acc; }, {});
    const recentPRs = Object.entries(byExercise).reduce((acc, [name, sets]) => {
      const allTime  = Math.max(...sets.map(s => s.weight));
      const recent   = sets.filter(s => s.date >= cutoff14Str);
      if (recent.length === 0) return acc;
      const recentMax = Math.max(...recent.map(s => s.weight));
      if (recentMax >= allTime) {
        const prEntry = recent.find(s => s.weight === recentMax);
        acc.push({ name, weight: recentMax, date: prEntry.date, reps: prEntry.reps });
      }
      return acc;
    }, []).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);

    // Muscle balance (last 30 days)
    const cutoff30 = new Date(now); cutoff30.setDate(now.getDate() - 30);
    const cutoff30Str    = cutoff30.toISOString().split("T")[0];
    const recentStrength = workouts.filter(w => w.type === "strength" && w.date >= cutoff30Str);
    const subgroupCounts = {};
    recentStrength.forEach(w => {
      const entry = EXERCISE_LOOKUP[w.name.toLowerCase()];
      if (entry) {
        subgroupCounts[entry.p] = (subgroupCounts[entry.p] || 0) + TIER_WEIGHT.primary;
        (entry.s || []).forEach(sg => { subgroupCounts[sg] = (subgroupCounts[sg] || 0) + TIER_WEIGHT.strong; });
        (entry.t || []).forEach(sg => { subgroupCounts[sg] = (subgroupCounts[sg] || 0) + TIER_WEIGHT.stabilizer; });
      } else {
        const subs = MUSCLE_SUBGROUPS[w.muscleGroup];
        if (subs?.[0]) subgroupCounts[subs[0]] = (subgroupCounts[subs[0]] || 0) + TIER_WEIGHT.primary;
      }
    });
    const muscleBalance = MUSCLE_GROUPS.map(group => ({
      group,
      subgroups: MUSCLE_SUBGROUPS[group].map(sg => ({ name: sg, count: Math.round((subgroupCounts[sg] || 0) * 20) / 20 })),
    }));
    const maxMuscle = Math.max(...muscleBalance.flatMap(g => g.subgroups.map(s => s.count)), 1);

    // Next plan (rotation-aware)
    const activeProgram  = programs.find(p => p.id === activeProgramId) || programs[0] || null;
    const runnablePlans  = activeProgram?.plans.filter(p => p.type !== "rest" && (p.exercises?.length || 0) > 0) || [];
    let nextPlan = runnablePlans[0] || null;
    if (runnablePlans.length > 1 && lastRunPlanId) {
      const lastIdx = runnablePlans.findIndex(p => p.id === lastRunPlanId);
      if (lastIdx !== -1) nextPlan = runnablePlans[(lastIdx + 1) % runnablePlans.length];
    }

    // This week sessions
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
    const weekStartStr = weekStart.toISOString().split("T")[0];
    const weekSessions = new Set(workouts.filter(w => w.date >= weekStartStr).map(w => w.date)).size;

    return { streak, recentPRs, muscleBalance, maxMuscle, nextPlan, weekSessions, activeProgram };
  }, [workouts, programs, activeProgramId, lastRunPlanId]);

  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} style={{
      padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
      fontWeight: 600, fontSize: 13, letterSpacing: "0.01em",
      background: tab === id ? C.accent : "transparent",
      color: tab === id ? "#1e1d1b" : C.muted,
      transition: "all 0.15s",
    }}>{label}</button>
  );

  const loadingScreen = (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@800&display=swap');
        @keyframes fulcrum-pulse { 0%,100% { opacity: 0.35; } 50% { opacity: 0.8; } }
      `}</style>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 800, color: C.blue, animation: "fulcrum-pulse 1.6s ease-in-out infinite" }}>Fulcrum</div>
    </div>
  );

  if (authLoading || dataLoading) return loadingScreen;

  if (!user) return <AuthPage />;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'Inter', sans-serif", paddingBottom: 80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');
        * { box-sizing: border-box; }
        input, select, button { font-family: inherit; }
        input::placeholder { color: #6a6c6e; }
        input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.8); opacity: 0.5; }
        select option { background: #262424; color: #f0eeeb; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #403e3e; border-radius: 4px; }
      `}</style>

      {/* Header */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "18px 24px 0", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", color: "#7aabd4" }}>
                Fulcrum
              </div>
              <div style={{ color: C.muted, fontSize: 11, marginTop: 2, letterSpacing: "0.06em", textTransform: "uppercase" }}>Strength & cardio tracker</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => signOut(auth)} title="Sign out" style={{ background: "transparent", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", padding: "8px 6px", letterSpacing: "0.04em", fontFamily: "inherit" }}>
                Sign out
              </button>
              <button onClick={() => setShowSettings(true)} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 9, color: C.blue, fontSize: 17, cursor: "pointer", padding: "8px 12px", lineHeight: 1 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
              </button>
              <Btn variant="primary" onClick={() => setShowLog(true)} style={{ padding: "9px 18px" }}>+ LOG</Btn>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {tabBtn("dashboard", "Dashboard")}
            {tabBtn("plans",     "Plans")}
            {tabBtn("library",   "Library")}
            {tabBtn("history",   "History")}
            {tabBtn("progress",  "Progress")}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 24px 0" }}>
        {tab === "dashboard" && <Dashboard dashStats={dashStats} settings={settings} startRun={startRun} onShowLibrary={() => setTab("library")} />}

        {tab === "library" && (
          <ExerciseLibraryTab
            customExercises={customExercises}
            userExerciseOverrides={userExerciseOverrides}
            programs={programs}
            activeProgramId={activeProgramId}
            onAddCustomExercise={addCustomExercise}
            onUpdateCustomExercise={updateCustomExercise}
            onDeleteCustomExercise={deleteCustomExercise}
            onSetExerciseOverride={setExerciseOverride}
            onResetExerciseOverride={resetExerciseOverride}
            advancedMode={!!settings.advancedMode}
          />
        )}

        {tab === "plans" && (
          <ProgramsTab
            programs={programs}
            activeProgramId={activeProgramId}
            onSetActive={setActiveProgramId}
            onStartPlan={startRun}
            onNewProgram={() => setEditingProg(null)}
            onEditProgram={prog => setEditingProg(prog)}
            onDeleteProgram={deleteProgram}
            onNewPlan={programId => { setAddingToProg(programId); setEditingPlan(null); }}
            onAddRestDay={addRestDay}
            onEditPlan={(programId, plan) => setEditingPlan({ plan, programId })}
            onDeletePlan={deletePlan}
            onReorderPlans={reorderPlans}
            onReorderPrograms={reorderPrograms}
            lastRunPlanId={lastRunPlanId}
            settings={settings}
          />
        )}

        {tab === "history" && (
          <HistoryTab workouts={workouts} filterType={filterType} setFilterType={setFilterType} settings={settings} />
        )}

        {tab === "progress" && (
          <ProgressionTab workouts={workouts} settings={settings} programs={programs} activeProgramId={activeProgramId} customExercises={customExercises} userExerciseOverrides={userExerciseOverrides} />
        )}
      </div>

      {/* Modals */}
      {showLog && (
        <WorkoutForm onAdd={w => setWorkouts(p => [w, ...p])} onClose={() => setShowLog(false)} settings={settings} customExercises={customExercises} />
      )}
      {(editingPlan || addingToProg) && (
        <PlanBuilder
          plan={editingPlan?.plan || null}
          onSave={plan => savePlan(editingPlan?.programId || addingToProg, plan)}
          onClose={() => { setEditingPlan(null); setAddingToProg(null); }}
          customExercises={customExercises}
          onAddCustomExercise={addCustomExercise}
        />
      )}
      {editingProg !== undefined && (
        <ProgramBuilder program={editingProg} onSave={saveProgram} onClose={() => setEditingProg(undefined)} />
      )}
      {showSettings && (
        <SettingsModal settings={settings} onChange={updateSetting} onClose={() => setShowSettings(false)} />
      )}
      {activeRun && (
        <WorkoutRunner
          plan={activeRun}
          onFinish={finishRun}
          onClose={() => setActiveRun(null)}
          settings={settings}
          workouts={workouts}
          customExercises={customExercises}
          userExerciseOverrides={userExerciseOverrides}
          onUpdateCustomExercise={updateCustomExercise}
          onSetExerciseOverride={setExerciseOverride}
        />
      )}
    </div>
  );
}
